import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { FixedMixCreate, MixCreate, SteelGrade } from "store/api/generatedApi";

export const AddFixedMixButton = ({
  steelGrades,
  mixes,
  mixGroups,
  disabled,
  onAdd,
}: {
  steelGrades: SteelGrade[];
  mixes: FixedMixCreate[];
  mixGroups: string[][];
  disabled: boolean;
  onAdd: (gradeNames: string[], recipe: MixCreate) => void;
}) => {
  const { t } = useTenantTranslation();
  const [open, setOpen] = useState(false);
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [templateGrade, setTemplateGrade] = useState("");
  const availableGrades = steelGrades
    .filter(
      (grade) => !mixes.some((mix) => mix.steel_grade_name === grade.name)
    )
    .map((grade) => grade.name);
  const selected = availableGrades.filter((name) =>
    selectedGrades.includes(name)
  );
  return (
    <>
      <Button
        startIcon={<AddIcon />}
        disabled={disabled || availableGrades.length === 0}
        onClick={() => {
          setSelectedGrades([]);
          setTemplateGrade("");
          setOpen(true);
        }}
      >
        {t("addPredefinedMix")}
      </Button>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("addPredefinedMix")}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            <Autocomplete
              multiple
              options={availableGrades}
              value={selected}
              onChange={(_, names) => {
                setSelectedGrades(names);
              }}
              renderInput={(parameters) => (
                <TextField {...parameters} label={t("steelGrades")} />
              )}
            />
            <TextField
              select
              label={t("predefinedMixTemplate")}
              value={templateGrade}
              onChange={(event) => {
                setTemplateGrade(event.target.value);
              }}
            >
              <MenuItem value="">{t("emptyPredefinedMix")}</MenuItem>
              {mixGroups.map((names) => (
                <MenuItem key={names[0]} value={names[0]}>
                  {names.join(", ")}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            disabled={disabled || selected.length === 0}
            onClick={() => {
              const template = mixes.find(
                (mix) => mix.steel_grade_name === templateGrade
              );
              onAdd(selected, {
                baskets: template?.mix.baskets ?? [],
                furnace: template?.mix.furnace ?? [],
                thresholds: [],
              });
              setOpen(false);
            }}
          >
            {t("add")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
